import { useCallback } from "react";
import { useLocalStorage } from "@rehooks/local-storage";

import { StrictModeState } from "utils/strictMode";

import { STRICT_MODE_ENABLED_KEY } from "../constants";

// 2 hours
const EXPIRATION_MS = 2 * 60 * 60 * 1000;

const useStrictModeWithExpiry = () => {
  const [storedState, setStoredState] = useLocalStorage<StrictModeState>(STRICT_MODE_ENABLED_KEY, {
    enabled: false,
  });

  const setEnabled = useCallback(
    (enabled: boolean) => {
      setStoredState({
        enabled,
        enabledAt: enabled ? Date.now() : undefined,
      });

      // Refresh the page to apply StrictMode changes
      // StrictMode wrapper is only evaluated on app initialization
      window.location.reload();
    },
    [setStoredState]
  );

  const cleanupExpiredStrictMode = useCallback(() => {
    // If enabled but missing enabledAt (manual manipulation), treat as expired
    if (storedState.enabled && !storedState.enabledAt) {
      setStoredState({ enabled: false });
      return undefined;
    }

    const isExpired =
      storedState.enabled &&
      storedState.enabledAt &&
      Date.now() - storedState.enabledAt > EXPIRATION_MS;

    if (isExpired) {
      setStoredState({ enabled: false });
    }
  }, [storedState, setStoredState]);

  return {
    isEnabled: storedState.enabled,
    setEnabled,
    cleanupExpiredStrictMode,
  };
};

export default useStrictModeWithExpiry;
